import { useRequiredContext } from "@redotech/react-util/context";
import BarChartIcon from "@redotech/redo-icons/bar-chart-10.svg";
import BarChartFilledIcon from "@redotech/redo-icons/bar-chart-10_filled.svg";
import CreditCard01Icon from "@redotech/redo-icons/credit-card-01.svg";
import CreditCard01FilledIcon from "@redotech/redo-icons/credit-card-01_filled.svg";
import CurrencyDollarIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import CurrencyDollarFilledIcon from "@redotech/redo-icons/currency-dollar-circle_filled.svg";
import DataflowIcon from "@redotech/redo-icons/dataflow-03.svg";
import DataflowFilledIcon from "@redotech/redo-icons/dataflow-03_filled.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import SettingsFilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import ShieldDollarIcon from "@redotech/redo-icons/shield-dollar.svg";
import ShieldDollarFilledIcon from "@redotech/redo-icons/shield-dollar_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useMatch } from "react-router-dom";

export function useFinanceNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);

  const primaryTab: LinkPrimaryTab = {
    id: "finance",
    hasNotification: false,
    UnfilledIcon: CurrencyDollarIcon,
    FilledIcon: CurrencyDollarFilledIcon,
    type: "link",
    href: routes.disputesDashboardHref,
    pathRoot: routes.financeBaseUrl,
    friendlyName: "Finance",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "finance",
    name: "Finance",
    NavContent: SecondaryFinanceNavigation,
  };

  return { primaryTab, secondaryNavigationData };
}

const SecondaryFinanceNavigation = memo(function SecondaryFinanceNavigation() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Finance",
  });

  const disputesBaseUrl = routes.financeBaseUrl + "/disputes";
  const anyDisputeChildSelected = useMatch(`${disputesBaseUrl}/*`);

  return (
    <TreeContext.Provider value={nodeId}>
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.disputesDashboardHref }}
        anyChildSelected={!!anyDisputeChildSelected}
        beta
        FilledIcon={ShieldDollarFilledIcon}
        friendlyName="Reclaim"
        id="disputes"
        UnfilledIcon={ShieldDollarIcon}
      >
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesDashboardHref }}
          FilledIcon={BarChartFilledIcon}
          friendlyName="Dashboard"
          id="disputes-dashboard"
          UnfilledIcon={BarChartIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesChargebacksHref }}
          FilledIcon={CreditCard01FilledIcon}
          friendlyName="Chargeback disputes"
          id="disputes-chargebacks"
          UnfilledIcon={CreditCard01Icon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesSettingsHref }}
          FilledIcon={SettingsFilledIcon}
          friendlyName="Settings"
          id="disputes-settings"
          UnfilledIcon={SettingsIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesIntegrationsHref }}
          FilledIcon={DataflowFilledIcon}
          friendlyName="Integrations"
          id="disputes-integrations"
          UnfilledIcon={DataflowIcon}
        />
      </RedoSecondaryNavItem>
    </TreeContext.Provider>
  );
});
